import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Paper,
  Popper,
  PopperPlacementType
} from "@mui/material";
import { debounce } from "lodash";
import React, {
  useState,
} from "react";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import { useFormContext } from "react-hook-form";
import AutocompleteTextField from "./AutocompleteTextField";
import { AutocompleteResponse } from "entities/UIModel";
import LightTooltip from "controls/global/light-tooltip";
import { REGEX_NONE_ASCII } from "utils/shared";
import {
    colors,
    boxShadows,
} from "theme/defaultStyle";

interface Props<T> {
  label: string;
  loading?: boolean;
  name: string;
  value?: any;
  freeSolo?: boolean;
  forcePopupIcon?: boolean;
  minLengthBeforeSearch?: number;
  disabled?: boolean;
  maxLength?: number;
  stateName?: string;
  hasError?: boolean;
  milisecondsToDelay?: number;
  onFocus?: (event: React.ChangeEvent<any>) => void;
  onBlur?: (event: React.ChangeEvent<any>) => void;
  onBlurForm?: () => void;
  onValueChanged: (value: any) => void;
  onGetOptions?: (inputText: string) => Promise<T[]>;
  onTypeInput?: (
    inputText: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  getOptionSelected: (option: any, value: any) => boolean;
  getOptionLabel?: (option: any) => string;
  options?: AutocompleteResponse[];
  open?: boolean;
  debounceTime?: number;
  fieldRef?: any;
  popperPlacement?: PopperPlacementType | undefined;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any,
    state: any,
    ownerState: any,
  ) => React.ReactNode;
  resetDefaultValue?: string;
  autoSelectOnExactMatch?: boolean;
  preferFilteredOptions?: boolean;
  clearOnBlur?: boolean;
}

const AutocompleteInput = <T,>({
  label,
  loading = false,
  name,
  value,
  minLengthBeforeSearch = 1,
  freeSolo = true,
  forcePopupIcon,
  disabled = false,
  maxLength,
  stateName,
  hasError = false,
  milisecondsToDelay = 500,
  onBlur,
  onBlurForm,
  onFocus,
  onValueChanged,
  onGetOptions,
  onTypeInput,
  getOptionLabel,
  renderOption,
  options,
  debounceTime = 500,
  fieldRef,
  popperPlacement,
  resetDefaultValue,
  autoSelectOnExactMatch = false,
  preferFilteredOptions,
  clearOnBlur = false,
  ...rest
}: Props<T>) => {
  const [shrinkable, setShrinkable] = useState<boolean>(false);
  const [_options, setOptions] = React.useState<any[]>([]);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [, { setBypassDefaultLoader }] = useProcessStatusTracking();
  const { getValues } = useFormContext();
  const relatedStateField = stateName
    ? getValues(`${stateName}` as const)
    : undefined;

  const handleOnChange = (_: React.ChangeEvent<any>, newValue: any) => {
    const opts = _options ? _options : [];
    setOptions(
      newValue
        ? [newValue, ...opts?.filter((option) => option !== newValue)]
        : opts
    );
    setShrinkable(true);
    onValueChanged && onValueChanged(newValue);
  };

  const handleOnBlur = (e: React.ChangeEvent<any>) => {
    const currentValue = e.target.value;
    setShrinkable(currentValue || hasError);
    if (currentValue !== value) {
      if (!freeSolo) {
        if (autoSelectOnExactMatch) {
          if (currentValue) {
            const opts = _options || options || [];
            const matchedOption = opts.find((option) => {
              const optionLabel = getOptionLabel ? getOptionLabel(option) : option;
              return optionLabel?.toLowerCase()?.trim() === currentValue?.toLowerCase()?.trim();
            });

            if (matchedOption) {
              onValueChanged(matchedOption);
            }
          } else {
            onValueChanged("");
          }
        } else if (clearOnBlur && !currentValue) {
          onValueChanged("");
        }
        return;
      }

      onValueChanged(currentValue);
    }

    onBlur && onBlur(e);
    onBlurForm && onBlurForm();
  };

  const handleOnInputChange = async (
    _: React.ChangeEvent<any>,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (!newInputValue) setOptions([]);
    const newInputValue1 = newInputValue?.replace(REGEX_NONE_ASCII,"")?.trim();
    if (newInputValue1.length >= minLengthBeforeSearch) {
      if (onGetOptions) {
        setBypassDefaultLoader(true);
        const _options = await onGetOptions(newInputValue1);
        setTimeout(() => setBypassDefaultLoader(false), 500);
        setOptions(_options);
      }
    }
    onTypeInput?.(newInputValue, reason);
  };

  React.useEffect(() => {
    if (clearOnBlur) {
      const hasValue = value && (typeof value === 'string' ? value.trim() !== '' : (value.text || value.value));
      setShrinkable(hasError || Boolean(hasValue));
    } else {
      setShrinkable(hasError || value);
    }
  }, [value, hasError, clearOnBlur]);

  React.useEffect(() => {
    setOptions([]);
  }, [relatedStateField]);

  const handleFocus = () => {
    setShrinkable(true);
  };

  const handleOpenTooltip = (event: any) => {
    const inputElem = event.target;
    const compare =
      inputElem && inputElem?.scrollWidth > inputElem?.clientWidth;
    setIsTooltipOpen(compare);
  };

  return (
    <>
      <Autocomplete
        getOptionLabel={getOptionLabel}
        options={preferFilteredOptions && _options.length > 0 ? _options : (options || _options || [])}
        filterOptions={(x) => x}
        autoComplete
        disabled={disabled}
        onMouseEnter={handleOpenTooltip}
        onMouseLeave={() => setIsTooltipOpen(false)}
        includeInputInList
        filterSelectedOptions
        disableClearable
        noOptionsText="NO DATA FOUND"
        loading={loading}
        value={value || ""}
        onChange={handleOnChange}
        onFocus={onFocus}
        onBlur={handleOnBlur}
        freeSolo={freeSolo}
        forcePopupIcon={forcePopupIcon !== undefined ? forcePopupIcon : freeSolo}
        handleHomeEndKeys={false}
        onInputChange={debounce(
          (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => handleOnInputChange(event, value, reason),
          debounceTime
        )}
        {...rest}
        renderInput={(params: AutocompleteRenderInputParams) => {
          let defaultValue = params.inputProps.value;
          if (resetDefaultValue === name && disabled) {
            defaultValue = params.inputProps.value || value;
          }

          const tooltipValue = value && typeof value === 'object' 
            ? (getOptionLabel ? getOptionLabel(value) : (value?.text || value?.value || ""))
            : (value || "");

          return (
            <LightTooltip title={tooltipValue} open={isTooltipOpen} arrow>
              <AutocompleteTextField
                {...params}
                name={name}
                label={label}
                error={hasError}
                onFocus={handleFocus}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                }}
                inputProps={{
                  ...params.inputProps,
                  value: defaultValue,
                  maxLength: maxLength,
                  "aria-errormessage": hasError ? "rhfvalidationerror" : "",
                }}
                InputLabelProps={{
                  shrink: Boolean(shrinkable),
                }}
                variant="outlined"
                inputRef={fieldRef}
              />
            </LightTooltip>
          );
        }}
        renderOption={renderOption}
        PaperComponent={(props) => (
          <Paper
            {...props}
            style={{
              padding: 0,
              margin: 0,
              border: `0px solid ${colors.blue01}`,
              boxShadow: `${boxShadows.boxShadow03}`
            }}
          />
        )}
        PopperComponent={({ open, ...props }) => {
          const hasOptions = preferFilteredOptions && _options.length > 0 
            ? _options.length > 0 
            : (options?.length || _options?.length || loading);
          return (
            <Popper
              {...props}
              open={hasOptions ? open : false}
              placement={popperPlacement}
            />
          );
        }}
      />
    </>
  );
};

export default AutocompleteInput;
